import {
  getGatewayServerUrl,
  getPlatformHost,
  getServerEnvironment,
} from 'shared-lib';
import { getAuthTokenKey, getUserManager, UserManager } from 'app-core';

const authErrorMessage =
  'The current user is not authorized to access this resource';
const userManager = getUserManager();

async function getAuthToken(
  userManager: UserManager | null,
  authTokenKey: string | null,
): Promise<string | null> {
  try {
    if (authTokenKey == null) {
      return null;
    }
    let authorization = localStorage.getItem(authTokenKey);

    if (userManager) {
      const user = await userManager.getUser();
      if (user && user.expired) {
        console.log(
          `Relay Network. User Is Signed Out. Attempting Silent Sign In`,
        );
        await userManager.signinSilent();
        authorization = user.access_token;
        localStorage.setItem(authTokenKey, authorization);
        return authorization;
      }
    }
    return authorization;
  } catch (e) {
    return null;
  }
}

const fetchPlus = (url, options = {}, retries) =>
  fetch(url, options)
    .then(res => {
      if (res.ok) {
        return res;
      }
      if (retries > 0) {
        return fetchPlus(url, options, retries - 1);
      }
      throw new Error(res.statusText);
    })
    .catch(error => console.error(error.message));

async function request(
  apiUrl: string,
  authorization: string | null,
  query: string,
  variables: any,
): Promise<any> {
  const headers: any = {
    'Content-Type': 'application/json',
  };

  if (authorization) {
    headers.authorization = `Bearer ${authorization}`;
  }

  const response = await fetchPlus(
    apiUrl,
    {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        query,
        variables,
      }),
    },
    5,
  );

  if (!response) {
    return null;
  }

  const json = await response.json();

  return json;
}

let serverApiUrl;

export async function fetchRelay(
  params: any,
  variables: any,
  _cacheConfig: any,
) {
  if (!serverApiUrl) {
    serverApiUrl = getGatewayServerUrl(
      getPlatformHost(),
      getServerEnvironment(),
    );
  }

  const authTokenKey = getAuthTokenKey();
  const authorization = await getAuthToken(userManager, authTokenKey);

  if (!authorization) {
    console.log(`Relay Network Is Anonymous`);
  }

  let json = await request(serverApiUrl, authorization, params.text, variables);

  if (!json) {
    return null;
  }

  // GraphQL returns exceptions (for example, a missing required variable) in the "errors"
  // property of the response. If any exceptions occurred when processing the request,
  // throw an error to indicate to the developer what went wrong.
  if (Array.isArray(json.errors)) {
    if (json.errors.length === 0) {
      return json;
    }

    const error = json.errors[0];

    if (error['message']) {
      const message: string = error['message'].toString();

      //TODO: Set Next URL As Current

      if (message && message.includes(authErrorMessage) && userManager) {
        await userManager.signinSilent();

        const user = await userManager.getUser();

        if (user) {
          const authorization = user.access_token;
          json = await request(
            serverApiUrl,
            authorization,
            params.text,
            variables,
          );
        }
      } else {
        throw new Error(`GraphQL Error: ${params.name} - ${error.message}`);
      }
    }
    console.error(json.errors);

    return null;
  }

  // Otherwise, return the full payload.
  return json;
}
